import { useEffect, useMemo, useState } from 'react';
import { Button, Center, Group, Loader, Select, Stack, TextInput, Title } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useProjectAnalyses } from '@/api/analyses';
import { errorMessage } from '@/api/errors';
import { useProjects } from '@/api/projects';
import { useRuns, useRunTypes, type RunFilters } from '@/api/runs';
import { EmptyState } from '@/components/EmptyState';
import { usePageTitle } from '@/components/usePageTitle';
import { RunsTable } from '@/features/runs/RunsTable';
import { runFilters, validateRunsSearch, type RunsSearch } from '@/features/runs/runsSearch';

const STATES = ['created', 'pending', 'running', 'succeeded', 'failed', 'cancelled'];
/** Shortcuts beside the plain states: everything forun still works on, and today's failures. */
const PRESETS = ['active', 'failed24h'] as const;

export const Route = createFileRoute('/_staff/staff/runs/')({ validateSearch: validateRunsSearch, component: RunsPage });

function RunsPage() {
  const { t, i18n } = useTranslation('runs');
  const lang = i18n.language as 'cs' | 'en';
  usePageTitle(t('title'));
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<RunsSearch>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const [q, setQ] = useState(search.q ?? '');
  const [debounced] = useDebouncedValue(q.trim(), 300);
  useEffect(() => {
    if ((search.q ?? '') !== debounced) set({ q: debounced || undefined });
    // Only the debounced box writes `q`; a URL change by navigation does not re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const { data: types } = useRunTypes();
  const projects = useProjects({ includeArchived: true });
  const analyses = useProjectAnalyses(search.project);
  const filters = useMemo(() => runFilters(search), [search]);

  const projectOptions = (projects.data ?? []).map((p) => ({ value: p.public_id, label: `${p.name} · ${p.organization.name}` }));
  if (search.project && !projectOptions.some((o) => o.value === search.project)) projectOptions.push({ value: search.project, label: search.project });
  const analysisOptions = (analyses.data ?? []).map((a) => ({ value: a.public_id, label: a.name[lang] || a.name.cs }));
  if (search.analysis && !analysisOptions.some((o) => o.value === search.analysis)) analysisOptions.push({ value: search.analysis, label: search.analysis });
  const stateOptions = [
    { group: t('filters.presets'), items: PRESETS.map((p) => ({ value: p, label: t(`filters.preset.${p}`) })) },
    { group: t('columns.state'), items: STATES.map((s) => ({ value: s, label: s })) },
  ];

  return (
    <Stack gap="lg">
      <Title order={1}>{t('title')}</Title>
      <Group>
        <TextInput placeholder={t('filters.q')} aria-label={t('filters.q')} value={q} onChange={(e) => setQ(e.currentTarget.value)} w={240} />
        <Select
          placeholder={t('columns.state')}
          aria-label={t('columns.state')}
          data={stateOptions}
          value={search.state ?? null}
          onChange={(v) => set({ state: v ?? undefined })}
          clearable
          w={200}
        />
        <Select
          placeholder={t('columns.type')}
          aria-label={t('columns.type')}
          data={(types ?? []).map((x) => x.key)}
          value={search.type ?? null}
          onChange={(v) => set({ type: v ?? undefined })}
          clearable
          searchable
          w={200}
        />
        <Select
          placeholder={t('filters.project')}
          aria-label={t('filters.project')}
          data={projectOptions}
          value={search.project ?? null}
          onChange={(v) => set({ project: v ?? undefined, analysis: undefined })}
          clearable
          searchable
          nothingFoundMessage={t('filters.nothingFound')}
          w={280}
        />
        <Select
          placeholder={t('filters.analysis')}
          aria-label={t('filters.analysis')}
          data={analysisOptions}
          value={search.analysis ?? null}
          onChange={(v) => set({ analysis: v ?? undefined })}
          disabled={!search.project && !search.analysis}
          clearable
          searchable
          nothingFoundMessage={t('filters.nothingFound')}
          w={240}
        />
      </Group>
      <RunsList key={JSON.stringify(filters)} filters={filters} />
    </Stack>
  );
}

/** Keyed by the filters, so a new filter starts again from the newest page. */
function RunsList({ filters }: { filters: RunFilters }) {
  const { t } = useTranslation('runs');
  const runs = useRuns(filters);
  if (runs.isPending)
    return (
      <Center py="xl">
        <Loader color="forest" />
      </Center>
    );
  if (runs.isError) return <EmptyState title={errorMessage(runs.error)} />;
  if (!runs.data?.length) return <EmptyState title={t('empty')} />;
  return (
    <Stack gap="sm">
      <RunsTable runs={runs.data} />
      {runs.hasMore && (
        <Center>
          <Button variant="default" loading={runs.loadingMore} onClick={runs.loadMore}>
            {t('loadMore')}
          </Button>
        </Center>
      )}
    </Stack>
  );
}
